import React, { useEffect, useState } from 'react';
import { useDebounce } from 'react-use';
import { Stack } from '@grnhse/seedling/lib/birch/layouts/stack_v2';
import { Grid } from '@grnhse/seedling/lib/birch/layouts/grid_v2';
import { SimpleSingleDropdown } from '@grnhse/seedling/lib/birch/components/forms/dropdown';
import { Item } from '@grnhse/seedling/lib/birch/components/forms/dropdown/item';
import IconButton from '@grnhse/seedling/lib/birch/components/buttons/icon_buttons';
import CloseIcon from '@grnhse/seedling/lib/birch/icons/close';
import AdvancedFilter from 'talent_matching/components/filters/advanced_filter';
import { FilterType } from 'talent_matching/types';
import {
  CurrencyFilter,
  DateFilter,
  MultiSelectFilter,
  NumberFilter,
  SingleSelectFilter,
} from 'talent_matching/components/filters/fields';
import TextInput from '@grnhse/seedling/lib/birch/components/forms/text_input';
import styled from 'styled-components';
import { withLocalePrefix } from 'shared/utils/translation';
import { SecondaryBody } from '@grnhse/seedling/lib/birch/typography';
import Tokens from '@grnhse/seedling/lib/birch/tokens';

const t = withLocalePrefix('talent_matching.filter_row.advanced_filters');

const FieldWrapper = styled(Grid)<{ width?: string; display?: string }>`
  width: ${(props) => props.width || 'auto'};
  max-width: ${(props) => props.width || 'none'};
  min-width: ${(props) => props.width || 'auto'};
  display: ${(props) => props.display || 'flex'};
  flex-wrap: nowrap;
  flex-direction: row;
  align-items: center;

  && div {
    padding-right: 0;
  }
`;

const ValueWrapper = styled(Grid)`
  flex: 1 1 0;
  min-width: 200px;
  display: flex;
  flex-wrap: nowrap;
  align-items: center;

  && div {
    padding-right: 0;
  }
`;

const OperatorLabel = styled.div`
  display: flex;
  align-items: center;
  height: 40px;
  padding: 0 ${Tokens.spacing['1']};
`;

const RemoveButton = styled.div`
  align-self: center;
  display: flex;
  align-items: center;
  height: 40px;
  margin-left: ${Tokens.spacing['1']};
  flex-shrink: 0;
`;

type Props = {
  filter: AdvancedFilter;
  onChange: (filter: AdvancedFilter) => void;
  onRemove: () => void;
  typeOptions: Item[];
};

const isFilterType = (item: Item | null): item is FilterType => {
  return Boolean(item && (item as FilterType).operators);
};

const SingleFilter = ({ filter, onChange, onRemove, typeOptions }: Props) => {
  const [textValue, setTextValue] = useState<string>(
    typeof filter.value === 'string' ? filter.value : ''
  );

  useEffect(() => {
    setTextValue(typeof filter.value === 'string' ? filter.value : '');
  }, [filter.value]);

  const operators = filter.type?.operators || [];
  const selectedOperator =
    operators.find((operator) => operator.value === filter.operator?.value) ||
    null;

  const onTypeChange = (item: Item | null) => {
    const selectedItem = isFilterType(item) ? item : null;
    const newOperator = selectedItem?.operators[0] || null;
    const defaultValue = {
      short_text: '',
      long_text: '',
      yes_no: null,
      single_select: null,
      multi_select: [],
      currency: 'USD$:',
      number: ':',
      date: null,
      user: null,
      default: null,
    }[selectedItem?.valueType || 'default'];

    onChange(
      new AdvancedFilter({
        type: selectedItem,
        operator: newOperator,
        value: defaultValue,
      })
    );
  };

  const onValueChange = (newValue: string | Item | Item[] | null) => {
    onChange(
      new AdvancedFilter({
        ...filter,
        value: newValue,
      })
    );
  };

  const shouldDebounceTextValue =
    filter.operator?.value === 'contains' ||
    filter.type?.valueType === 'short_text' ||
    filter.type?.valueType === 'long_text';

  useDebounce(
    () => {
      if (shouldDebounceTextValue) {
        onValueChange(textValue);
      }
    },
    500,
    [textValue, shouldDebounceTextValue]
  );

  const onOperatorChange = (item: Item | null) => {
    const newFilter = new AdvancedFilter({
      ...filter,
      operator: item,
    });

    const valueType = filter.type?.valueType || '';
    const oldOperatorValue = filter.operator?.value || '';
    const newOperatorValue = item?.value || '';

    // Only reset value when switching between operators that expect different value types
    const oldIsContains = oldOperatorValue === 'contains';
    const newIsContains = newOperatorValue === 'contains';

    if (['user', 'single_select', 'multi_select'].includes(valueType)) {
      // Switching TO 'contains' from item-based operator
      if (newIsContains && !oldIsContains) {
        newFilter.value = '';
        setTextValue('');
      }
      // Switching FROM 'contains' to item-based operator
      else if (!newIsContains && oldIsContains) {
        newFilter.value = valueType === 'multi_select' ? [] : null;
        setTextValue('');
      }
      // Otherwise, keep the existing value when switching between compatible operators
    }

    onChange(newFilter);
  };

  const onSelectedItemsChange = (changes: { selectedItems: Item[] }) => {
    onValueChange(changes.selectedItems);
  };

  const onSelectedItemChange = (e: { selectedItem?: Item<string> | null }) => {
    onValueChange(e.selectedItem || null);
  };

  const renderValueField = () => {
    if (!filter.type) {
      return (
        <FieldWrapper width="100%" display="unset">
          <SimpleSingleDropdown
            ariaLabel={t('value')}
            placeholder={t('value')}
            onSelectedItemChange={() => null}
            items={[]}
            disabled={!filter.type}
          />
        </FieldWrapper>
      );
    }

    const operator = filter.operator?.value || '';

    if (operator === 'contains') {
      return (
        <TextInput
          ariaLabel={t('value')}
          placeholder={t('value')}
          value={textValue}
          onChange={(e) => setTextValue(e.target.value)}
        />
      );
    }

    if (['is_defined', 'is_not_defined'].includes(operator)) {
      return null;
    }

    switch (filter.type.valueType) {
      case 'short_text':
      case 'long_text':
        return (
          <TextInput
            ariaLabel={t('value')}
            placeholder={t('value')}
            value={textValue}
            onChange={(e) => setTextValue(e.target.value)}
          />
        );
      case 'yes_no':
        return (
          <SingleSelectFilter
            filter={filter}
            onSelectedItemChange={onSelectedItemChange}
          />
        );
      case 'single_select':
      case 'multi_select':
      case 'user':
        return (
          <MultiSelectFilter
            filter={filter}
            onSelectedItemsChange={onSelectedItemsChange}
          />
        );
      case 'currency':
        return <CurrencyFilter filter={filter} onValueChange={onValueChange} />;
      case 'number':
        return <NumberFilter filter={filter} onValueChange={onValueChange} />;
      case 'date':
        return <DateFilter filter={filter} onValueChange={onValueChange} />;
      default:
        return null;
    }
  };

  return (
    <Stack
      direction="row"
      alignItems="center"
      flexWrap="nowrap"
      spacing={1}
      sx={{ width: '100%' }}
    >
      <FieldWrapper width="200px">
        <SimpleSingleDropdown
          ariaLabel={t('type')}
          placeholder={t('type')}
          selectedItem={filter.type}
          onSelectedItemChange={onTypeChange}
          items={typeOptions}
        />
      </FieldWrapper>
      <FieldWrapper width="160px">
        {operators.length === 1 ? (
          <OperatorLabel>
            <SecondaryBody>{operators[0].label}</SecondaryBody>
          </OperatorLabel>
        ) : (
          <SimpleSingleDropdown
            ariaLabel={t('operator')}
            placeholder={t('operator')}
            selectedItem={selectedOperator}
            onSelectedItemChange={onOperatorChange}
            items={operators}
            disabled={!filter.type}
            clearable={false}
          />
        )}
      </FieldWrapper>

      <ValueWrapper>{renderValueField()}</ValueWrapper>
      <RemoveButton>
        <IconButton
          label={t('remove')}
          onClick={onRemove}
          icon={CloseIcon}
          size="sm"
        />
      </RemoveButton>
    </Stack>
  );
};

export default SingleFilter;
